/* ==========================================================================
   The one time scale.
   --------------------------------------------------------------------------
   Ribbons, plots and rulers all bind to the same [from, to] window and the
   same tick ladder, so a state band and a metric trace can be stacked and
   read against one x-axis. Everything here is UTC: the console never shows a
   local hour without saying so.
   ========================================================================== */

const SEC = 1000;
const MIN = 60 * SEC;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** Steps an operator actually thinks in. No 2.5-minute ticks. */
const STEPS = [
  SEC, 2 * SEC, 5 * SEC, 10 * SEC, 15 * SEC, 30 * SEC,
  MIN, 2 * MIN, 5 * MIN, 10 * MIN, 15 * MIN, 30 * MIN,
  HOUR, 2 * HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR,
  DAY, 2 * DAY, 7 * DAY, 14 * DAY, 28 * DAY,
];

export interface Tick {
  t: number;
  label: string;
  /** 0–1 across the window. */
  f: number;
}

const P2 = (n: number) => String(n).padStart(2, '0');

function labelFor(t: number, step: number): string {
  const d = new Date(t);
  if (step < MIN) return `${P2(d.getUTCHours())}:${P2(d.getUTCMinutes())}:${P2(d.getUTCSeconds())}`;
  if (step < DAY) return `${P2(d.getUTCHours())}:${P2(d.getUTCMinutes())}`;
  return `${P2(d.getUTCMonth() + 1)}-${P2(d.getUTCDate())}`;
}

/**
 * Ticks on round UTC boundaries inside [from, to], aiming for `target` of
 * them. Boundary-aligned rather than evenly-divided, so 12:00 lands on 12:00
 * and two panels showing the same window put their gridlines in the same
 * places.
 */
export function ticks(from: number, to: number, target = 6): Tick[] {
  const span = Math.max(1, to - from);
  const want = span / Math.max(1, target);
  const step = STEPS.find((s) => s >= want) ?? STEPS[STEPS.length - 1]!;
  const out: Tick[] = [];
  for (let t = Math.ceil(from / step) * step; t <= to; t += step) {
    out.push({ t, label: labelFor(t, step), f: (t - from) / span });
  }
  return out;
}

/** Fraction 0–1 of `t` across the window, clamped. */
export function frac(t: number, from: number, to: number): number {
  if (to <= from) return 0;
  return Math.max(0, Math.min(1, (t - from) / (to - from)));
}

/** `HH:MM:SSZ` — the readout beside a crosshair. Always Zulu. */
export function axisClock(t: number): string {
  const d = new Date(t);
  return `${P2(d.getUTCHours())}:${P2(d.getUTCMinutes())}:${P2(d.getUTCSeconds())}Z`;
}
